// RegisterCompany.jsx - Versão corrigida

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Mail,
    Lock,
    Phone,
    Building2,
    CreditCard,
    Upload,
    Check,
    ArrowLeft,
    MapPin,
    FileText,
    Eye,
    EyeOff,
} from "lucide-react";
import "./RegisterCompany.scss";
import { register, updateProfile } from "../../services/authService";
import { uploadAvatar } from "../../services/cloudinaryService";
import Toast from "../../Components/Toast/Toast";
import { CnpjValidator } from "../../utils/cnpjValidator";

export default function RegisterCompany() {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [cnpj, setCnpj] = useState(""); // Armazena o CNPJ com formatação para exibição
    const [cnpjClean, setCnpjClean] = useState(""); // Armazena o CNPJ limpo (apenas números)
    const [address, setAddress] = useState("");
    const [description, setDescription] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [cnpjError, setCnpjError] = useState(null);

    const [phoneError, setPhoneError] = useState(null);
    const [phoneValidated, setPhoneValidated] = useState(false);

    const [toast, setToast] = useState({ message: "", type: "success" });
    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    const VALID_DDDS = [
        "11", "12", "13", "14", "15", "16", "17", "18", "19",
        "21", "22", "24", "27", "28",
        "31", "32", "33", "34", "35", "37", "38",
        "41", "42", "43", "44", "45", "46", "47", "48", "49",
        "51", "53", "54", "55",
        "61", "62", "63", "64", "65", "66", "67", "68", "69",
        "71", "73", "74", "75", "77", "79",
        "81", "82", "83", "84", "85", "86", "87", "88", "89",
        "91", "92", "93", "94", "95", "96", "97", "98", "99",
    ];

    const formatPhone = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 11);
        if (digits.length <= 2) return digits;
        if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    };

    const validatePhone = (value) => {
        const digits = value.replace(/\D/g, "");
        if (digits.length !== 11) {
            return "O número precisa ter DDD + 9 dígitos (celular).";
        }
        const ddd = digits.slice(0, 2);
        if (!VALID_DDDS.includes(ddd)) {
            return "DDD inválido.";
        }
        if (digits[2] !== "9") {
            return "Número de celular deve começar com 9 após o DDD.";
        }
        return null;
    };

    const handlePhoneChange = (e) => {
        setPhone(formatPhone(e.target.value));
        setPhoneValidated(false);
        setPhoneError(null);
    };

    const handleValidatePhone = () => {
        const error = validatePhone(phone);
        if (error) {
            setPhoneError(error);
            setPhoneValidated(false);
        } else {
            setPhoneError(null);
            setPhoneValidated(true);
        }
    };

    // Função corrigida para validar CNPJ
    const validateCnpj = (value) => {
        // Remove formatação para validar
        const digits = CnpjValidator.clean(value);

        if (digits.length === 0) {
            return "CNPJ é obrigatório";
        }

        if (digits.length !== 14) {
            return "CNPJ deve ter 14 dígitos";
        }

        if (!CnpjValidator.isValid(value)) {
            return "CNPJ inválido. Verifique os números e tente novamente.";
        }

        return null;
    };

    // Função corrigida para lidar com mudanças no CNPJ
    const handleCnpjChange = (e) => {
        const rawValue = e.target.value;
        // Remove tudo que não é número
        const digits = rawValue.replace(/\D/g, '').slice(0, 14);
        
        // Formata para exibição
        const formatted = CnpjValidator.format(digits);
        
        setCnpj(formatted);
        setCnpjClean(digits); // Mantém apenas os números
        setCnpjError(null);
    };

    // Valida ao perder o foco
    const handleCnpjBlur = () => {
        if (cnpjClean) {
            // Passa o CNPJ com formatação para validação
            const error = validateCnpj(cnpj);
            setCnpjError(error);
        }
    };

    // Função para gerar CNPJ válido para teste (REMOVER EM PRODUÇÃO)
    const generateValidCnpj = () => {
        // Gera um CNPJ válido para teste
        const cnpj = CnpjValidator.generateValidCnpj();
        setCnpj(CnpjValidator.format(cnpj));
        setCnpjClean(cnpj);
        setCnpjError(null);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const validatePassword = (value) => {
        const strongPasswordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

        if (!strongPasswordRegex.test(value)) {
            return "A senha deve conter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma minúscula, um número e um caractere especial.";
        }
        return null;
    };

    const getErrorMessage = (error) => {
        const data = error?.response?.data;

        if (!data) return "Erro ao cadastrar. Tente novamente.";
        if (typeof data === "string") return data;
        if (data.message) return data.message;
        if (data.error) return data.error;

        if (data.errors) {
            const messages = Object.values(data.errors).flat();
            if (messages.length > 0) return messages.join(" ");
        }

        return "Erro ao cadastrar. Tente novamente.";
    };

    const handleRegister = async () => {
        if (!phoneValidated) {
            showToast("Valide seu telefone antes de continuar.", "error");
            return;
        }

        // Valida CNPJ usando a versão limpa
        const cnpjValidationError = validateCnpj(cnpj);
        if (cnpjValidationError) {
            showToast(cnpjValidationError, "error");
            setCnpjError(cnpjValidationError);
            return;
        }

        const passwordError = validatePassword(password);
        if (passwordError) {
            showToast(passwordError, "error");
            return;
        }

        if (password !== confirmPassword) {
            showToast("As senhas não coincidem.", "error");
            return;
        }

        setSubmitting(true);

        try {
            // Envia o CNPJ limpo (apenas números) para o backend
            const result = await register({
                name: companyName,
                email,
                password,
                confirmPassword,
                cnpj: cnpjClean, // Envia apenas os números!
                phone,
                userType: 2
            });

            localStorage.setItem("token", result.token);
            localStorage.setItem("userId", result.id);
            localStorage.setItem("userType", result.userType);

            const uploadFailures = [];

            try {
                await updateProfile(result.id, {
                    name: companyName,
                    phone,
                    cnpj: cnpjClean, // Envia apenas os números para o perfil também
                    address,
                    bio: description,
                });
            } catch (err) {
                console.error("Erro ao atualizar perfil:", err);
                uploadFailures.push("endereço/descrição");
            }

            if (logoFile) {
                try {
                    await uploadAvatar(result.id, logoFile);
                } catch (err) {
                    console.error("Erro ao enviar logo:", err);
                    uploadFailures.push("logo");
                }
            }

            if (uploadFailures.length > 0) {
                showToast(
                    `Cadastro realizado, mas não foi possível enviar: ${uploadFailures.join(", ")}. ` +
                    "Você pode reenviar depois em Editar Perfil.", "info"
                );
            } else {
                showToast("Cadastro realizado com sucesso!", "success");
            }

            navigate("/dashboard-company");

        } catch (error) {
            console.error("Erro detalhado do backend:", error?.response?.data);
            showToast(getErrorMessage(error), "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="register-page">
            <div className="register-overlay" />

            <div className="register-card">
                <div className="register-header">
                    <h1>Crie sua conta Empresarial</h1>
                    <p>Contrate os melhores profissionais.</p>
                </div>

                <div className="auth-tabs">
                    <Link to="/" className="tab-btn">
                        Entrar
                    </Link>
                    <button className="tab-btn active">Cadastrar</button>
                </div>

                <div className="register-step">
                    <Link to="/register" className="back-link">
                        <ArrowLeft size={18} />
                        Voltar
                    </Link>

                    {/* Email */}
                    <div className="form-group">
                        <label>Email *</label>
                        <div className="input-wrapper">
                            <Mail size={18} />
                            <input
                                type="email"
                                placeholder="seu@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Nome da Empresa */}
                    <div className="form-group">
                        <label>Nome da Empresa *</label>
                        <div className="input-wrapper">
                            <Building2 size={18} />
                            <input
                                type="text"
                                placeholder="Nome Fantasia"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Senha */}
                    <div className="form-group">
                        <label>Senha *</label>
                        <div className="input-wrapper">
                            <Lock size={18} />
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Crie uma senha forte"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        <small>Mín. 8 caracteres, com maiúscula, minúscula, número e símbolo.</small>
                    </div>

                    {/* Confirmar Senha */}
                    <div className="form-group">
                        <label>Confirmar Senha *</label>
                        <div className="input-wrapper">
                            <Lock size={18} />
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Digite a mesma senha novamente"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Telefone */}
                    <div className="form-group">
                        <label>Telefone (WhatsApp) *</label>
                        <div className="phone-group">
                            <div className="input-wrapper">
                                <Phone size={18} />
                                <input
                                    type="text"
                                    placeholder="(11) 99999-9999"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                />
                            </div>
                            <button
                                type="button"
                                className={`btn-validate ${phoneValidated ? "is-valid" : ""}`}
                                onClick={handleValidatePhone}
                                disabled={phoneValidated}
                            >
                                <Check size={16} />
                                {phoneValidated ? "Validado" : "Validar"}
                            </button>
                        </div>
                        {phoneError && <small className="field-error">{phoneError}</small>}
                        {phoneValidated && <small className="field-success">Número válido</small>}
                        {!phoneError && !phoneValidated && <small>Requer validação</small>}
                    </div>

                    {/* CNPJ - VERSÃO CORRIGIDA */}
                    <div className="form-group">
                        <label>CNPJ *</label>
                        <div className="input-wrapper">
                            <CreditCard size={18} />
                            <input
                                type="text"
                                placeholder="00.000.000/0001-00"
                                value={cnpj}
                                onChange={handleCnpjChange}
                                onBlur={handleCnpjBlur}
                                className={cnpjError ? "input-error" : ""}
                            />
                        </div>
                        {cnpjError && (
                            <small className="field-error">{cnpjError}</small>
                        )}
                        {/* {!cnpjError && cnpjClean && (
                            <small className="field-success">
                                CNPJ válido
                                <span style={{ fontSize: '0.7rem', display: 'block', color: '#666' }}>
                                    (enviado como: {cnpjClean})
                                </span>
                            </small>
                        )} */}
                        {!cnpjError && !cnpj && (
                            <small>Ex: 12.345.678/0001-99</small>
                        )}
                        {/* Botão de teste - REMOVER EM PRODUÇÃO */}
                        {/* <button
                            type="button"
                            onClick={generateValidCnpj}
                            style={{
                                marginTop: '8px',
                                fontSize: '12px',
                                background: '#f0f0f0',
                                border: '1px solid #ddd',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Testar com CNPJ válido
                        </button> */}
                    </div>

                    {/* Logo */}
                    <div className="form-group">
                        <label>Logo da Empresa</label>
                        <label className="upload-box" htmlFor="logo-upload-input">
                            {logoPreview ? (
                                <img
                                    src={logoPreview}
                                    alt="Preview da logo"
                                    className="upload-box__preview"
                                />
                            ) : (
                                <Upload size={28} />
                            )}
                            <span>{logoFile ? logoFile.name : "Selecionar Imagem"}</span>
                            <input
                                id="logo-upload-input"
                                type="file"
                                accept="image/*"
                                onChange={handleLogoChange}
                            />
                        </label>
                    </div>

                    {/* Descrição */}
                    <div className="form-group">
                        <label>Descrição da Empresa (Atuação / Sobre)</label>
                        <div className="textarea-wrapper">
                            <FileText size={18} />
                            <textarea
                                rows="5"
                                placeholder="Conte um pouco sobre sua empresa, que tipo de evento realiza, etc."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Endereço */}
                    <div className="form-group">
                        <label>Sede / Endereço Principal</label>
                        <div className="input-wrapper">
                            <MapPin size={18} />
                            <input
                                type="text"
                                placeholder="Ex: Av. Paulista, 1000 - São Paulo, SP"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                            />
                        </div>
                    </div>

                    <button
                        className="btn-submit full-width"
                        onClick={handleRegister}
                        disabled={submitting}
                    >
                        {submitting ? "Enviando..." : "Finalizar Cadastro"}
                    </button>
                </div>

                <div className="register-footer">
                    <span>Já possui uma conta?</span>
                    <Link to="/login">Fazer login</Link>
                </div>
            </div>

            <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ message: "", type: "success" })}
            />
        </div>
    );
}